import { Button, DateTimeInput, Form, FormField, FormInput, FormLabel, Label, Switch } from '@/components/ui';
import DateRangePicker from '@/components/ui/date-range-picker';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ActivityIndicator, View, Text } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { addAvailabilityFormSchema, getRepeatFrequencyText } from './add-availability-helper';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { useState, useEffect } from 'react';

// Grey, so it reads as "someone else's" against the coloured selection.
const busyDotColor = '#8E8E93';

// Opens the spinner on a sensible hour rather than on whatever time it is right now.
const localTimeAt = (hours: number) => {
  const date = new Date();
  date.setHours(hours, 0, 0, 0);
  return date;
};

interface AvailabilityFormProps {
  form: UseFormReturn<z.infer<typeof addAvailabilityFormSchema>>;
  isAvailability: boolean;
  onSubmit: (data: z.infer<typeof addAvailabilityFormSchema>, startDate: string, endDate: string) => void;
  selectedAvailability?: z.infer<typeof addAvailabilityFormSchema>;
  /** For a period that has already started: saving is refused anyway, so don't offer it. */
  disableSubmit?: boolean;
  /** Days already covered by another period, as 'yyyy-MM-dd'. Shown as dots on the picker. */
  busyDays?: string[];
}

export default function AvailabilityForm({ form, isAvailability, onSubmit, selectedAvailability, disableSubmit, busyDays }: AvailabilityFormProps) {
  const [start_date, setStartDate] = useState<string | null>(selectedAvailability?.start_date || null);
  const [end_date, setEndDate] = useState<string | null>(selectedAvailability?.end_date || null);
  const [dateError, setDateError] = useState<string | null>(null);

  // Update the form values when dates change
  useEffect(() => {
    if (start_date) {
      form.setValue("start_date", start_date);
      setDateError(null);
    }
    if (end_date) {
      form.setValue("end_date", end_date);
      setDateError(null);
    }
  }, [start_date, end_date, form]);

  // may as well set type to holiday until we have a tentative type
  useEffect(() => {
    if (!isAvailability && !form.getValues("type")) {
      form.setValue("type", "0");
    }
  }, [isAvailability, form]);

  const insets = useSafeAreaInsets();
  const contentInsets = {
    top: insets.top,
    bottom: insets.bottom,
    left: 12,
    right: 12,
  }

  const color = form.watch("type") === "2" ? '#FFB347' : (isAvailability ? '#77DD77' : '#FF6961');

  const recurrenceRule = form.watch('recurrence_rule');
  const isRepeating = recurrenceRule && recurrenceRule !== 'Never';

  const handleSubmit = (data: z.infer<typeof addAvailabilityFormSchema>) => {
    if (!start_date || !end_date) {
      setDateError("Please select both start and end dates");
      return;
    }
    onSubmit(data, start_date, end_date);
  };

  const isDateRangeSelected = !!start_date && !!end_date;

  return (
    <View className="mb-8">
      <Form {...form}>
        <FormLabel className='mb-4'>Select {isAvailability ? 'Availability' : 'Unavailability'}</FormLabel>
        {!isAvailability && (
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <Select
                onValueChange={(option) => field.onChange(option?.value || '')}
                className='mb-4'
                value={field.value ? { label: field.value === "0" ? 'Holiday' : 'Tentative', value: field.value } : undefined}
              >
                <SelectTrigger className='w-[250px]'>
                  <SelectValue
                    className='text-foreground text-sm native:text-lg'
                    placeholder='Select a type'
                  />
                </SelectTrigger>
                <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                  <SelectGroup>
                    {!isAvailability && (
                      <SelectItem label="Holiday" value="0">
                        Holiday
                      </SelectItem>
                    )}
                    {/* <SelectItem label="Tentative" value="2">
                    Tentative
                  </SelectItem> */}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        )}
        <FormLabel className="mb-1">Date Range <Text className="text-red-500">*</Text></FormLabel>
        <DateRangePicker
          initialRange={selectedAvailability ? [selectedAvailability.start_date!, selectedAvailability.end_date!] : []}
          onSuccess={(s, e) => {
            setStartDate(s);
            setEndDate(e);
          }}
          theme={{ markColor: color, markTextColor: 'white' }}
          busyDays={busyDays}
          busyColor={busyDotColor}
        />
        {!!busyDays?.length && (
          <View className="flex-row items-center mt-2">
            <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: busyDotColor }} />
            <Text className="ml-2 text-xs text-muted-foreground">
              Dotted days are already booked as {isAvailability ? 'available' : 'unavailable'} — you can't book over them.
            </Text>
          </View>
        )}
        {dateError && (
          <Text className="text-red-500 text-sm mt-1">{dateError}</Text>
        )}
        <FormField
          control={form.control}
          name="all_day"
          render={({ field }) => (
            <View className="flex flex-row items-center justify-between mt-4">
              <Label className="mr-2">All day</Label>
              <Switch checked={field.value ?? false} onCheckedChange={field.onChange} nativeID='all_day' />
            </View>
          )}
        />
        {!form.watch('all_day') && (
          <>
            <View className='mt-2'>
              <FormField
                control={form.control}
                name="start_time"
                render={({ field }) => (
                  <DateTimeInput
                    {...field}
                    label="Start time"
                    value={field.value || null}
                    defaultValue={localTimeAt(9)}
                    mode="time"
                  />
                )}
              />
            </View>
            <View className='mt-2'>
              <FormField
                control={form.control}
                name="end_time"
                render={({ field }) => (
                  <DateTimeInput
                    {...field}
                    label="End time"
                    value={field.value || null}
                    defaultValue={localTimeAt(17)}
                    mode="time"
                  />
                )}
              />
            </View>
            <Text className="mt-2 text-xs text-muted-foreground">
              Times are UK time. Leave them blank to cover the whole day.
            </Text>
          </>
        )}
        <FormLabel className='mt-2'>Repeats</FormLabel>
        <FormField
          control={form.control}
          name="recurrence_rule"
          render={({ field }) => (
            <Select
              onValueChange={(option) => field.onChange(option?.value || '')}
              value={field.value ? { label: field.value, value: field.value } : { label: "Never", value: "Never" }}
            >
              <SelectTrigger className='w-[250px]'>
                <SelectValue
                  className='text-foreground text-sm native:text-lg'
                  placeholder='Select a repeat option'
                />
              </SelectTrigger>
              <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                <SelectGroup>
                  <SelectItem label="Never" value="Never">
                    Never
                  </SelectItem>
                  <SelectItem label="Daily" value="Daily">
                    Daily
                  </SelectItem>
                  <SelectItem label="Weekly" value="Weekly">
                    Weekly
                  </SelectItem>
                  <SelectItem label="Monthly" value="Monthly">
                    Monthly
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        />
        {isRepeating && (
          <>
            <FormLabel className='mt-4'>
              End Repeat Count
              <Text className="text-red-500"> *</Text>
            </FormLabel>
            <FormField
              control={form.control}
              name="end_repeat"
              render={({ field }) => (
                <FormInput
                  {...field}
                  value={field.value}
                  keyboardType="numeric"
                  placeholder="e.g., 10"
                />
              )}
            />

            <FormLabel className='mt-4'>
              Repeat every {getRepeatFrequencyText(form.watch("recurrence_rule"))}
              <Text className="text-red-500"> *</Text>
            </FormLabel>
            <FormField
              control={form.control}
              name="end_repeat_frequency"
              render={({ field }) => (
                <FormInput
                  {...field}
                  value={field.value}
                  keyboardType="numeric"
                  placeholder="e.g., 1"
                />
              )}
            />
          </>
        )}
        <Button
          size="default"
          variant="crew"
          onPress={form.handleSubmit(handleSubmit)}
          className={`mt-4 ${!isDateRangeSelected || disableSubmit ? 'opacity-50' : ''}`}
          disabled={disableSubmit || !isDateRangeSelected || form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? (
            <ActivityIndicator size="small" />
          ) : (
            "Save"
          )}
        </Button>
      </Form>
    </View>
  )
}